/**
 * @typedef {Object} VerifyTokenOptions
 * @property {string|(() => string)} backendDomain - Base URL (must end in `/`) or a function returning it.
 * @property {typeof fetch} [fetch] - Fetch implementation (defaults to `globalThis.fetch`).
 * @property {(info: { claims: any, idToken: string, audience?: string }) => any} [onTokenVerified] - Called with the parsed response body plus the raw id token / audience used for verification; its return value resolves the promise.
 * @property {() => void} [onLoggedOut] - Called on any failure path.
 * @property {{ error?: (...a: any[]) => void, warn?: (...a: any[]) => void }} [logger]
 */

/**
 * Create a bound `verifyToken(token, audience?)` function.
 *
 * Replaces the jQuery-based original with `fetch`. Hosts provide `onTokenVerified`
 * to perform MSAL account creation or any other post-verification bookkeeping.
 *
 * @param {VerifyTokenOptions} options
 * @returns {(token: string, audience?: string) => Promise<any>}
 */
export function createVerifyToken(options) {
    const {
        backendDomain,
        fetch: fetchImpl = typeof globalThis !== 'undefined' && globalThis.fetch
            ? globalThis.fetch.bind(globalThis)
            : undefined,
        onTokenVerified,
        onLoggedOut,
        logger = console,
    } = options || {};

    if (!backendDomain) {
        throw new Error('createVerifyToken: `backendDomain` is required');
    }
    if (typeof fetchImpl !== 'function') {
        throw new Error('createVerifyToken: a `fetch` implementation is required');
    }

    const resolveBase = () =>
        typeof backendDomain === 'function' ? backendDomain() : backendDomain;

    return async function verifyToken(token, audience) {
        const base = resolveBase();
        const url = `${base}validate${audience ? `?aud=${encodeURIComponent(audience)}` : ''}`;

        logger.debug && logger.debug('[adxdb-bridge] verifyToken: sending GET to ' + url);

        try {
            const response = await fetchImpl(url, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
            });

            logger.debug && logger.debug('[adxdb-bridge] verifyToken: response status=' + response.status + ' ok=' + response.ok);

            if (!response.ok) {
                throw new Error(`verifyToken failed: HTTP ${response.status}`);
            }

            const claims = await response.json();
            logger.debug && logger.debug('[adxdb-bridge] verifyToken: claims received: ' + JSON.stringify(claims));
            return onTokenVerified ? onTokenVerified({ claims, idToken: token, audience }) : claims;
        } catch (err) {
            const details = [
                'URL: ' + url,
                'name: ' + err.name,
                'message: ' + err.message,
                'cause: ' + (err.cause != null ? JSON.stringify(err.cause) : 'none'),
                'stack: ' + (err.stack || 'n/a'),
            ].join('\n');
            logger.error && logger.error('[adxdb-bridge] verify Token ERROR\n' + details);
            if (typeof onLoggedOut === 'function') {
                try { onLoggedOut(); } catch (_) { /* swallow */ }
            }
            if (logger && typeof logger.error === 'function') {
                logger.error('verifyToken error:', err);
            }
            throw err;
        }
    };
}
